"use client";

import { useMemo } from "react";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { fetchClientBranding, type ClientBranding } from "@/lib/api/branding";
import {
  getNotificationPreferences,
  type NotificationPrefs,
} from "@/lib/api/notification-preferences";
import { listVehicles } from "@/lib/api/vehicles";
import { listGroups } from "@/lib/api/groups";
import { listZones } from "@/lib/api/zones";
import { listPois } from "@/lib/api/pois";
import { listDrivers } from "@/lib/api/drivers";
import { listUsers } from "@/lib/api/users";
import { listMapInputs } from "@/lib/api/inputs";
import { listCosts, listDeletedCosts, type CostFilters } from "@/lib/api/costs";
import { listCostCategories } from "@/lib/api/cost-categories";
import { listCostRecurrences } from "@/lib/api/cost-recurrences";
import { listCostDue, DEFAULT_DUE_WINDOW_DAYS } from "@/lib/api/cost-due";
import { listScheduledReportRuns, listScheduledReports } from "@/lib/api/scheduled-reports";
import { getUserPreferences, type UserPreferences } from "@/lib/api/user-preferences";
import { DEFAULT_CURRENCY, type CurrencyCode } from "@/lib/format/money";
import {
  listActivity,
  listActivitySummary,
  type ActivityFilters,
  type ActivityPage,
} from "@/lib/api/activity";
import type {
  Vehicle,
  VehicleGroup,
  Zone,
  POI,
  Cost,
  CostCategory,
  CostDue,
  CostRecurrence,
  DeletedCost,
  ActivityUserSummary,
  ScheduledReport,
  ScheduledReportRun,
} from "@/types/fleet";
import type { ClientDriver } from "@/lib/api/drivers";
import type { ClientUser } from "@/lib/api/users";
import type { InputListEntry } from "@/lib/telemetry/attrs";

/**
 * Shared cache for the reference lists. Six-plus views each fetched `/api/vehicles` and
 * `/api/groups` on mount, so moving between Tracking, History, Reports and the settings pages
 * issued the same upstream request over and over. Keying them here dedupes concurrent callers
 * and serves navigations from cache while a background refresh runs.
 */
export const queryKeys = {
  vehicles: ["vehicles"] as const,
  groups: ["groups"] as const,
  zones: ["zones"] as const,
  pois: ["pois"] as const,
  drivers: ["drivers"] as const,
  users: ["users"] as const,
  inputs: ["inputs"] as const,
  clientBranding: ["client-branding"] as const,
  costs: ["costs"] as const,
  costCategories: ["cost-categories"] as const,
  costRecurrences: ["cost-recurrences"] as const,
  costDue: ["cost-due"] as const,
  costLog: ["cost-log"] as const,
  costAttachments: ["cost-attachments"] as const,
  costRecurrenceLog: ["cost-recurrence-log"] as const,
  activity: ["activity"] as const,
  activitySummary: ["activity-summary"] as const,
  notificationPrefs: ["notification-prefs"] as const,
  scheduledReports: ["scheduled-reports"] as const,
  scheduledReportRuns: ["scheduled-report-runs"] as const,
  userPreferences: ["user-preferences"] as const,
};

/** Long enough to make navigation instant, short enough that edits show up promptly. */
export const LIST_STALE_TIME_MS = 60_000;

/**
 * Device input definitions are global reference data that changes when a device type is
 * reconfigured — never during a session. The server caches them for 10 minutes anyway
 * (`fetchInputMap`), so the browser holds them far longer than the editable lists above.
 */
export const INPUT_LIST_STALE_TIME_MS = 30 * 60_000;

type ListQueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, readonly string[]>,
  "queryKey" | "queryFn"
>;

function useListQuery<T>(
  queryKey: readonly string[],
  queryFn: () => Promise<T>,
  options?: ListQueryOptions<T>,
) {
  return useQuery({
    queryKey,
    queryFn,
    staleTime: LIST_STALE_TIME_MS,
    ...options,
  });
}

export const useVehiclesQuery = (options?: ListQueryOptions<Vehicle[]>) =>
  useListQuery(queryKeys.vehicles, listVehicles, options);

export const useGroupsQuery = (options?: ListQueryOptions<VehicleGroup[]>) =>
  useListQuery(queryKeys.groups, listGroups, options);

export const useZonesQuery = (options?: ListQueryOptions<Zone[]>) =>
  useListQuery(queryKeys.zones, listZones, options);

export const usePoisQuery = (options?: ListQueryOptions<POI[]>) =>
  useListQuery(queryKeys.pois, listPois, options);

export const useDriversQuery = (options?: ListQueryOptions<ClientDriver[]>) =>
  useListQuery(queryKeys.drivers, listDrivers, options);

export const useUsersQuery = (options?: ListQueryOptions<ClientUser[]>) =>
  useListQuery(queryKeys.users, listUsers, options);

/**
 * The `show_on_map` input definitions, so the browser can enrich the raw attrs the vehicle
 * WebSocket delivers. Only Live Tracking needs them; everything else reads attrs already
 * enriched by `/api/vehicles`.
 */
export const useMapInputsQuery = (options?: ListQueryOptions<Map<string, InputListEntry>>) =>
  useListQuery(queryKeys.inputs, listMapInputs, {
    staleTime: INPUT_LIST_STALE_TIME_MS,
    ...options,
  });

/**
 * The account's own logo and name for the sidebar header. Branding is fixed for the life of a
 * session, so it is held as long as the input definitions rather than the editable lists.
 *
 * The key carries the user id on purpose: `logout()` does not clear the query cache and
 * login/logout is a client-side navigation, so a shared key would show the previous account's
 * brand to the next one — the same hazard that keeps `loadLogo()` in `lib/export/pdf.ts`
 * unmemoized.
 */
export const useCostCategoriesQuery = (options?: ListQueryOptions<CostCategory[]>) =>
  useListQuery(queryKeys.costCategories, listCostCategories, options);

export const useCostRecurrencesQuery = (options?: ListQueryOptions<CostRecurrence[]>) =>
  useListQuery(queryKeys.costRecurrences, () => listCostRecurrences(), options);

/**
 * The reminder feed. Read by the Costs page, the notifications page and the sidebar badge at the
 * same time, so it goes through the shared cache like every other list.
 */
export const useCostDueQuery = (
  withinDays: number = DEFAULT_DUE_WINDOW_DAYS,
  options?: ListQueryOptions<CostDue[]>,
) =>
  useQuery({
    queryKey: [...queryKeys.costDue, String(withinDays)],
    queryFn: () => listCostDue(withinDays),
    staleTime: LIST_STALE_TIME_MS,
    ...options,
  });

/**
 * Costs are the one list that is fetched filtered, so the filters belong in the key — two date
 * ranges are two different results and must not share a cache entry. `useListQuery` takes a plain
 * `string[]` key, hence the local `useQuery`.
 */
export function useCostsQuery(
  filters: CostFilters,
  options?: Omit<UseQueryOptions<Cost[], Error, Cost[], readonly string[]>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: [...queryKeys.costs, JSON.stringify(filters)],
    queryFn: () => listCosts(filters),
    staleTime: LIST_STALE_TIME_MS,
    ...options,
  });
}

/**
 * Costs deleted in a period, newest first, one row per cost. Keyed under `costs` so the
 * invalidation after a cost write — a delete or a restore above all — refetches it too.
 */
export function useDeletedCostsQuery(
  range: { dateFrom: string; dateTo: string },
  options?: Omit<
    UseQueryOptions<DeletedCost[], Error, DeletedCost[], readonly string[]>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: [...queryKeys.costs, "deleted", range.dateFrom, range.dateTo],
    queryFn: async () => {
      // A cost deleted, restored and deleted again has two delete entries; only the latest counts.
      const latest = new Map<string, DeletedCost>();
      for (const cost of await listDeletedCosts(range)) {
        const seen = latest.get(cost.costId);
        if (!seen || cost.deletedAt > seen.deletedAt) latest.set(cost.costId, cost);
      }
      return [...latest.values()].sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
    },
    staleTime: LIST_STALE_TIME_MS,
    ...options,
  });
}

/**
 * The activity log, filtered and paged. Like costs, the filters belong in the key — two date
 * ranges are two different results.
 *
 * The user id is in the key as well, the way `useClientBrandingQuery` does it. `logout()` does not
 * clear the query cache and login is a client-side navigation, so a shared key would serve one
 * account's activity to whoever logged in next. That matters more here than anywhere else in the
 * app: this is the one list whose whole content is other people's behaviour.
 */
export function useActivityQuery(
  filters: ActivityFilters,
  options?: Omit<
    UseQueryOptions<ActivityPage, Error, ActivityPage, readonly string[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.activity, user?.id ?? "anon", JSON.stringify(filters)],
    queryFn: () => listActivity(filters),
    staleTime: LIST_STALE_TIME_MS,
    ...options,
  });
}

export function useActivitySummaryQuery(
  filters: Pick<ActivityFilters, "dateFrom" | "dateTo">,
  options?: Omit<
    UseQueryOptions<ActivityUserSummary[], Error, ActivityUserSummary[], readonly string[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.activitySummary, user?.id ?? "anon", JSON.stringify(filters)],
    queryFn: () => listActivitySummary(filters),
    staleTime: LIST_STALE_TIME_MS,
    ...options,
  });
}

/**
 * The signed-in user's own scheduled reports. Upstream scopes the list to the token's user, so the
 * user id is in the key like `useActivityQuery` — belt and braces on top of the cache clear at
 * every session boundary.
 */
export function useScheduledReportsQuery() {
  const { user } = useAuth();
  return useQuery<ScheduledReport[]>({
    queryKey: [...queryKeys.scheduledReports, user?.id ?? "anon"],
    queryFn: listScheduledReports,
    enabled: !!user,
    staleTime: LIST_STALE_TIME_MS,
    // A missing endpoint (404 until upstream ships it) is not worth three retries.
    retry: false,
  });
}

export function useScheduledReportRunsQuery(scheduleId: string | null) {
  return useQuery<ScheduledReportRun[]>({
    queryKey: [...queryKeys.scheduledReportRuns, scheduleId ?? ""],
    queryFn: () => listScheduledReportRuns(scheduleId ?? ""),
    enabled: !!scheduleId,
    // Runs land asynchronously after "Send now"; always refetch when the dialog opens.
    staleTime: 0,
    retry: false,
  });
}

export function useClientBrandingQuery() {
  const { user } = useAuth();
  return useQuery<ClientBranding>({
    queryKey: [...queryKeys.clientBranding, user?.id ?? "anon"],
    queryFn: fetchClientBranding,
    enabled: !!user,
    staleTime: INPUT_LIST_STALE_TIME_MS,
  });
}

/**
 * The signed-in user's toast preferences. Keyed on the user id like `useClientBrandingQuery`:
 * `logout()` does not clear the cache, and one account's choices must not decide which toasts the
 * next account sees. Only this user changes them, and the settings card writes the response back
 * into this entry, so it is held for the session rather than refetched.
 */
export function useNotificationPrefsQuery() {
  const { user } = useAuth();
  return useQuery<NotificationPrefs>({
    queryKey: notificationPrefsKey(user?.id),
    queryFn: getNotificationPreferences,
    enabled: !!user,
    staleTime: INPUT_LIST_STALE_TIME_MS,
  });
}

export function notificationPrefsKey(userId: string | undefined) {
  return [...queryKeys.notificationPrefs, userId ?? "anon"] as const;
}

export function userPreferencesKey(userId: string | undefined) {
  return [...queryKeys.userPreferences, userId ?? "anon"] as const;
}

/**
 * The signed-in user's own preferences. Keyed on the user id for the same reason as
 * `useNotificationPrefsQuery`; only this user changes them and the settings card writes the
 * response back into this entry, so it is held for the session. No retry: upstream answers 404
 * until `UserPreferences.php` ships, and that is a state, not a transient failure.
 */
export function useUserPreferencesQuery() {
  const { user } = useAuth();
  return useQuery<UserPreferences>({
    queryKey: userPreferencesKey(user?.id),
    queryFn: getUserPreferences,
    enabled: !!user,
    staleTime: INPUT_LIST_STALE_TIME_MS,
    retry: false,
  });
}

/** The currency a new cost or rule starts in: the user's preference, else `DEFAULT_CURRENCY`. */
export function useDefaultCurrency(): CurrencyCode {
  const { data } = useUserPreferencesQuery();
  return data?.defaultCurrency ?? DEFAULT_CURRENCY;
}

/**
 * Invalidators for use after a create/update/delete, so the affected lists refetch.
 * Vehicles and groups are linked (a vehicle carries a group id), so editing either
 * invalidates both.
 */
export function useListInvalidators() {
  const queryClient = useQueryClient();

  // Memoized so callers can safely list it in dependency arrays.
  return useMemo(
    () => ({
      vehicles: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
        void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
      },
      groups: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
        void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      },
      zones: () => void queryClient.invalidateQueries({ queryKey: queryKeys.zones }),
      pois: () => void queryClient.invalidateQueries({ queryKey: queryKeys.pois }),
      drivers: () => void queryClient.invalidateQueries({ queryKey: queryKeys.drivers }),
      users: () => void queryClient.invalidateQueries({ queryKey: queryKeys.users }),
      // Completing a planned cost advances its recurrence upstream, which moves the rule's
      // `next_due_*` and the reminder feed — so a cost write refetches both, not just the list.
      // Every cost/rule write also appends to an audit log, hence the two log keys.
      costs: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.costs });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costRecurrences });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costDue });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costLog });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costRecurrenceLog });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costAttachments });
      },
      // An upload or delete moves the cost's `attachment_count` and appends to its audit log, but
      // leaves recurrences and the due feed alone.
      costAttachments: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.costAttachments });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costs });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costLog });
      },
      costCategories: () =>
        void queryClient.invalidateQueries({ queryKey: queryKeys.costCategories }),
      costRecurrences: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.costRecurrences });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costDue });
        void queryClient.invalidateQueries({ queryKey: queryKeys.costRecurrenceLog });
      },
      // A manual send appends a run and moves `last_*` on the schedule.
      scheduledReports: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReports });
        void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReportRuns });
      },
    }),
    [queryClient],
  );
}
